import type { ReactNode } from 'react';
import { colors } from './styles/colors';

export const CONTENT_TYPES = {
  COURSE: 'Course',
  VIDEO: 'Video',
  QUIZ: 'Quiz',
  SLIDE: 'Slide',
  LAB: 'Lab',
  LINK: 'Link',
  LEARNING_PATH: 'Learning Path',
  SKILL_DIVE: 'Skill Dive',
};

export const PLAYLIST_TYPE = {
  SONAR_RECOMMENDED: 'sonar_recommended',
  SONAR_GOAL: 'sonar_goal',
  STANDARD: 'B2B',
};

export const PLAYLIST_METHOD = {
  CREATE: 'create',
  EDIT: 'edit',
  DUPLICATE: 'duplicate',
} as const;

export type PlaylistMethod = typeof PLAYLIST_METHOD[keyof typeof PLAYLIST_METHOD];

// Account-level playlist assignment cooldown window.
export const ACCOUNT_PLAYLIST_COOLDOWN_MS = 30000; // 30 seconds
export const PLAYLIST_RULE_SCOPE_ACCOUNT = 'account';
export const PLAYLIST_RULE_SCOPE_TEAM = 'team';

export const ADD_USERS_LABEL = 'Assign to users or teams';
export const ADD_USERS_ONLY_LABEL = 'Assign to users';
export const ASSIGN_PLAYLIST_LABEL = 'Assign playlist';
export const REMOVE_USER_FROM_PLAYLIST_LABEL = 'Remove user from playlist';
export const TEAM_INHERITED_REMOVE_TOOLTIP =
  'User assigned via team assignment;<br /> remove the team to unassign.';
export const ALL_USERS_ASSIGNED_TOOLTIP =
  'All users already assigned. You can\nremove assignments in the playlist.';

export const DELETE_PLAYLIST_CONFIRMATION_DESCRIPTION: ReactNode = `Are you sure you want to delete this playlist? This will permanently delete the playlist and unassign any assigned users.

This action can't be undone.`;

// Tooltip copy for account-assigned playlist UX.
export const ACCOUNT_PLAYLIST_TOOLTIPS = {
  accountPlaylist: 'Playlist assigned to the account',
  toggleReadOnly: 'Only the account owner\ncan update this setting',
  removeUserAO: 'User assigned via account;<br /> remove the account to unassign',
  removeUserTM: 'User assigned via account',
  removeUserAssignModal: 'Remove "Assign to account"<br /> to select individual users',
};

export const ACCOUNT_PLAYLIST_MESSAGES = {
  assigned: 'Playlist assigned to all licensed users',
  unassigned: 'Playlist unassigned from all licensed users',
};

export const ACCOUNT_PLAYLIST_ERROR_MESSAGE =
  'Unable to update account assignment. Please try again.';

export const ACCOUNT_PLAYLIST_TOGGLE_TOOLTIPS = {
  readOnly: ACCOUNT_PLAYLIST_TOOLTIPS.toggleReadOnly,
  cooldown: (seconds: number) =>
    `Please wait ${seconds} second${seconds === 1 ? '' : 's'}\n` +
    'before changing this setting again',
  loading: 'Updating playlist assignment',
  assignInfo: 'Assign to all users in the\naccount, including future ones',
  assignedInfo: 'Assigned to all users in the\naccount, including future ones',
  badge: "Playlist assigned by your\norganization's admin",
};

// Default account deadline (DEV-5599) copy — Figma 'Manage account deadline' drawer.
export const DEFAULT_DEADLINE_COPY = {
  entryPointLabel: 'Manage account deadline',
  drawerTitle: 'Manage account deadline',
  sectionTitle: 'Default account deadline',
  disclaimer:
    "This default deadline applies to all account members. You can override it for individual users. Custom deadlines aren't available because users may join at different times.",
  applyToLabel: 'Apply changes to:',
  saveButtonLabel: 'Save deadline',
};

type DeadlineApplyModeOption = {
  value: 'future' | 'all';
  label: string;
  description: string;
};

export const DEFAULT_DEADLINE_APPLY_MODE_OPTIONS: DeadlineApplyModeOption[] = [
  {
    value: 'future',
    label: 'Future assignments only',
    description: 'Existing deadlines will not be updated.',
  },
  {
    value: 'all',
    label: 'Existing and future assignments',
    description: 'Deadlines will be updated for all assigned users.',
  },
];

export const DEFAULT_DEADLINE_SUCCESS_MESSAGE = 'Account deadline updated';

export const DEFAULT_DEADLINE_ERROR_MESSAGE =
  'Unable to update the account deadline. Please try again.';

export const ACCOUNT_DEADLINE_DISABLE_CONFIRMATION_DESCRIPTION =
  "Turning off account assignment will also remove the default account deadline. This action can't be undone.";

// Team-level default deadline (DEV-5600) copy — Figma 'Manage team assignments' panel.
export const TEAM_DEFAULT_DEADLINE_COPY = {
  entryPointLabel: 'Manage team deadline',
  drawerTitle: 'Manage team deadline',
  sectionTitle: 'Default team deadline',
  disclaimer:
    "This default deadline applies to all members of this team. You can override it for individual users. Custom deadlines aren't available because users may join at different times.",
  applyToLabel: 'Apply changes to:',
  saveButtonLabel: 'Save deadline',
};

// Team-level apply-mode options (DEV-6548) — team-specific copy for "Existing and future".
export const TEAM_DEFAULT_DEADLINE_APPLY_MODE_OPTIONS: DeadlineApplyModeOption[] = [
  {
    value: 'future',
    label: 'Future assignments only',
    description: 'Existing deadlines will not be updated.',
  },
  {
    value: 'all',
    label: 'Existing and future assignments',
    description: 'Deadlines will be updated for all assigned team members.',
  },
];

export const TEAM_DEFAULT_DEADLINE_SUCCESS_MESSAGE = 'Team deadline updated';

export const TEAM_DEFAULT_DEADLINE_ERROR_MESSAGE =
  'Unable to update the team deadline. Please try again.';

export const TEAM_DEADLINES_PANEL_TITLE = 'Manage team assignments';

export const MANAGE_TEAM_DEADLINE_TOOLTIP = 'Manage team deadline';

export const REMOVE_TEAM_ASSIGNMENT_TOOLTIP = 'Remove team assignment';

export const REMOVE_TEAM_ASSIGNMENT_CONFIRMATION_DESCRIPTION =
  "Are you sure you want to remove this team's assignment from the playlist? This action can't be undone.";

export const LEARNING_PATH_SHORT_SLUG = 'path';

export const CONTENT_TYPE_COLORS = {
  course: colors.dataVisualization.purple.lighter,
  video: colors.dataVisualization.yellow.lighter,
  quiz: colors.dataVisualization.blue.lighter,
  slides: colors.dataVisualization.green.lighter,
  iframe: colors.dataVisualization.green.lighter,
  lab: colors.dataVisualization.orange.lighter,
  'ctf-lab': colors.dataVisualization.orange.lighter,
  collection: colors.dataVisualization.orange.lighter,
  link: colors.dataVisualization.gray.dark,
  'learning-path': colors.dataVisualization.pink.lighter,
  'learning-path-with-cert': colors.dataVisualization.pink.lighter,
  exercise: colors.dataVisualization.beige.lighter,
  'skill-check': colors.dataVisualization.red.lighter,
  assessment: colors.dataVisualization.blue.lighter,
  content: colors.dataVisualization.gray.dark,
};

export const CONTENT_TYPE_TEXT_COLORS = {
  course: 'var(--color-accent-purple)',
  video: 'var(--color-accent-yellow-dark)',
  quiz: 'var(--color-accent-red-dark)',
  slides: 'var(--color-accent-red-dark)',
  iframe: 'var(--color-accent-red-dark)',
  lab: 'var(--color-accent-red-dark)',
  'ctf-lab': 'var(--color-accent-red-dark)',
  collection: 'var(--color-accent-red-dark)',
  link: 'var(--light-gray-darker)',
  'learning-path': 'var(--color-danger-vivid)',
  'learning-path-with-cert': 'var(--color-danger-vivid)',
  exercise: 'var(--color-accent-red-dark)',
  'skill-check': 'var(--color-accent-red-dark)',
};

export const CONTENT_TAG_INDICATOR_GRADIENT = 'var(--secondary-promo-purple-gradient)';

export const CONTENT_THUMBNAIL_GRADIENT = 'var(--secondary-promo-purple-gradient)';

export const LEARNING_AREAS_TYPES = {
  cloud: { id: 'd3f82c70-f4e7-428e-afd7-4b1d28988e18', name: 'Cloud' },
  cyberSecurity: { id: '3e1aa06f-2e9f-4789-b50d-aa027ad8dcfa', name: 'Cyber Security' },
  dataScience: { id: 'db4652c5-2a80-4be8-a8f1-8852cf0e5230', name: 'Data Science' },
  development: { id: '47306190-b47d-46d5-bbf7-06ab3cdca942', name: 'Development' },
  itEssentials: { id: 'ff0cb823-5bec-4b3a-b096-415dcf6c0adb', name: 'IT Essentials' },
  networking: { id: 'e73fd5a8-2ead-4159-9a25-38b50ad4ab20', name: 'Networking' },
};

export const SKILL_DIVE_EXPERIENCE_TEXT = 'This lab is part of Skill Dive.';

export const LAB_EXPERIENCE_TEXT = 'This lab is part of Lab Experience.';

// Skill Dive labs run on the pta platform; els/github/azure labs are hosted
// elsewhere and stay Standard even when the entitlement flag is set.
export const SKILL_DIVE_LAB_TYPE = 'pta';

export const SKILL_DIVE_LAB = 'Skill Dive Lab';

export const LAB_EXPERIENCE_LAB = 'Lab Experience Lab';

export const LEARNING_PATH_TAG = 'Learning Path';

export const IFRAME_TAG = 'Slides';

export const LAB_TAG = 'Lab';

export const STANDARD_LAB_TAG = 'Standard Lab';

export const SKILL_DIVE_COLLECTION_TAG = 'Skill Dive Collection';

export const COURSE_TAG = 'Course';

export const VIDEO_TAG = 'Video';

export const LINK_TAG = 'Link';

export const CTF_TAG = 'CTF';

export const CTF_LAB_TAG = 'CTF Lab';

export const PREVIEW_COLLECTION_CTA = 'Preview Skill Dive Collection';

export const CONTACT_SALES_CTA = 'Contact sales to get access';

export const FLAGLESS_LAB_TOOLTIP = 'Lab has no flags and completes as soon as it is started';

export const HAS_SKILL_CHECK_LAB = 'Skill Check';

export const DRAG_TO_REARRANGE_LABEL = 'Drag to rearrange';

export const COLLECTION_LABS_TITLE = 'Labs';

export const COLLECTION_EMPTY_LABEL = 'This collection has no labs yet.';

// Add Content modal constants.
export const LINK_URL_REGEX = /^https?:\/\//;
export const DEFAULT_ADD_CONTENT_TAB = 'content';
export const CUSTOM_CONTENT_TAB = 'custom-content';
export const CUSTOM_CONTENT_PAGE_SIZE = 10;
export const MAX_CUSTOM_CONTENT_PAGES = 100;

export const CUSTOM_CONTENT_TYPE_ALIASES: Record<string, string> = {
  learningpath: 'learning-path',
  'learning-path-with-cert': 'learning-path',
  'standard-lab': 'lab',
  'skill-assessment': 'assessment',
  'skills-assessment': 'assessment',
};

export const ENABLED_CONTENT_TYPES = [CONTENT_TYPES.COURSE, CONTENT_TYPES.LEARNING_PATH];

export const CONTENT_TYPE_OPTIONS = [
  { label: 'All content', value: '' },
  { label: 'Courses', value: CONTENT_TYPES.COURSE },
  { label: 'Labs', value: CONTENT_TYPES.LAB },
  { label: 'Learning Paths', value: CONTENT_TYPES.LEARNING_PATH },
  { label: 'Videos', value: CONTENT_TYPES.VIDEO },
];

export const DIFFICULTY_OPTIONS = [
  { label: 'All levels', value: '' },
  { label: 'Informative', value: 'Informative' },
  { label: 'Novice', value: 'Novice' },
  { label: 'Professional', value: 'Professional' },
  { label: 'Advanced', value: 'Advanced' },
  { label: 'Expert', value: 'Expert' },
];

export const SKILL_DIVE_FILTER_LABEL = 'Skill Dive labs and collections';

export const SKILL_DIVE_FILTER_TOOLTIP =
  'Individual labs and lab collections from the Skill Dive library';

export const SKILL_DIVE_LABS_ONLY_LABEL = 'Skill Dive labs';

export const SKILL_DIVE_LABS_ONLY_TOOLTIP =
  'A curated selection of specialized labs within the INE platform.';

// With the flag off no collection can be returned, so the option must not
// advertise them. Copy follows what the filter can actually deliver.
export const getLabOptions = (collectionsEnabled = false) => [
  { label: 'All labs', value: '' },
  {
    label: 'Standard labs',
    value: 'false',
    tooltip: 'All labs on the INE Platform, except those in the Skill Dive library.',
  },
  {
    label: collectionsEnabled ? SKILL_DIVE_FILTER_LABEL : SKILL_DIVE_LABS_ONLY_LABEL,
    value: 'true',
    tooltip: collectionsEnabled ? SKILL_DIVE_FILTER_TOOLTIP : SKILL_DIVE_LABS_ONLY_TOOLTIP,
  },
];

export const LAB_OPTIONS = getLabOptions(false);

export const SKILL_CHECK_OPTIONS = [
  { label: 'All content', value: '' },
  {
    label: 'Content with Skill Check',
    value: 'true',
    tooltip: 'Show only content that includes a\n Skill Check lab(s) for skill validation',
  },
];

export const createInitialFilters = () => ({
  content_type: [] as string[],
  difficulty: [] as string[],
  'categories.name': [] as string[],
  'learning_areas.name': [] as string[],
});
